import moment from "moment"
import { Add, Check } from "@mui/icons-material"
import {
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
} from "@mui/material"

// The minimal instance shape the menu renders. Kept local (not the full DashboardInstance) so the
// presentational component stays decoupled from the resource DTO.
export interface SwitcherMenuInstance {
  instanceId: string
  slug: string
  name: string
  lastOpenedAt: string | null
}

export interface SwitcherOverflowMenuProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  instances: SwitcherMenuInstance[]
  // The instanceId OR slug of the active dashboard — matched against either so the checkmark lands
  // regardless of which form the URL used.
  currentId: string
  // Carries the chosen instance's slug (what the parent navigates to).
  onSelect: (slug: string) => void
  onNewDashboard: () => void
  isCreating: boolean
}

function formatLastOpened(lastOpenedAt: string | null): string {
  if (!lastOpenedAt) return "Never opened"
  return `Opened ${moment(lastOpenedAt).fromNow()}`
}

// The overflow menu: every instance (so nothing is unreachable when tabs scroll off) plus the
// "+ New dashboard" action. Presentational — selection/creation are delegated via callbacks.
export default function SwitcherOverflowMenu({
  anchorEl,
  open,
  onClose,
  instances,
  currentId,
  onSelect,
  onNewDashboard,
  isCreating,
}: SwitcherOverflowMenuProps) {
  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      <ListSubheader disableSticky>My dashboards</ListSubheader>
      {instances.map((instance) => {
        const isCurrent = instance.instanceId === currentId || instance.slug === currentId
        return (
          <MenuItem
            key={instance.instanceId}
            selected={isCurrent}
            onClick={() => {
              onSelect(instance.slug)
              onClose()
            }}
          >
            <ListItemIcon>{isCurrent && <Check fontSize="small" />}</ListItemIcon>
            <ListItemText primary={instance.name} secondary={formatLastOpened(instance.lastOpenedAt)} />
          </MenuItem>
        )
      })}
      <Divider />
      <MenuItem
        disabled={isCreating}
        onClick={() => {
          onNewDashboard()
          onClose()
        }}
      >
        <ListItemIcon><Add fontSize="small" /></ListItemIcon>
        <ListItemText primary="New dashboard" />
      </MenuItem>
    </Menu>
  )
}
